/**
 * Site JavaScript
 *
 * This file is bundled by esbuild and output to public/assets/js/main.js.
 * The base template includes it via <script src="/assets/js/main.js" defer>.
 *
 * Build:  npm run build
 * Watch:  npm run watch  (auto-rebuilds on change)
 */

import { animate } from "animejs";
import { isKnownUkPostcodeArea } from "./postcode-region";
import { computeEnergyEstimate, UNIT_RATE_PENCE, SELF_CONSUMPTION_SHARE_STORAGE_KEY, type CalcMode, type HouseholdSize } from "./energy-calculator";
import { APPLIANCE_PRESETS, findAppliancePreset, computeApplianceCost } from "./appliance-cost-calculator";
import { EV_PRESETS, findEvPreset, computeEvCostComparison } from "./ev-charging-cost-calculator";
import { PANEL_MODELS, BATTERY_MODELS, findPanelModel, findBatteryModel, type PanelModel, type BatteryModel } from "./panel-battery-comparison";

/**
 * Installer matching now runs server-side (POST /match, see the API repo's
 * handlers/match.go) so the full roster - coverage rules, partner status,
 * Trustpilot counts - never ships in this bundle. These types just describe
 * the wizard's own answers and the API's response shape; they used to be
 * imported from installers-data.ts, which is gone now that matching moved.
 */
type Product = "solar" | "battery" | "heat-pump" | "ev-charger";
type RoofType = "pitched" | "flat" | "not-sure";
type UsageLevel = "low" | "medium" | "high" | "not-sure";

/** Mirrors handlers/match.go's matchedInstaller: only what the match card renders. */
interface MatchedInstaller {
  name: string;
  tier?: "big" | "local";
  logo?: string;
  strengths?: string[];
  activeSince?: number;
}

// DOM ready helper
function ready(fn: () => void) {
  if (document.readyState !== "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

// In-memory only: a page reload re-checks, but that's rare within one form fill.
const postcodeExistsCache = new Map<string, boolean>();

/**
 * Confirms a postcode exists (not just well-formed) via postcodes.io, a free
 * no-key API over open ONS/Ordnance Survey data. Returns null on any network
 * failure or timeout so callers fall back to the offline checks instead of
 * blocking on a third-party outage.
 */
async function verifyPostcodeExists(cleanedPostcode: string): Promise<boolean | null> {
  const cached = postcodeExistsCache.get(cleanedPostcode);
  if (cached !== undefined) {
    return cached;
  }
  const url = `https://api.postcodes.io/postcodes/${encodeURIComponent(cleanedPostcode)}/validate`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(3000) });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    const result = data.result === true;
    postcodeExistsCache.set(cleanedPostcode, result);
    return result;
  } catch {
    return null;
  }
}

interface QuoteSubmitResult {
  matches?: MatchedInstaller[];
  name?: boolean;
  email?: boolean;
  phone?: boolean;
}

// In-memory only, mirrors postcodeExistsCache above. Avoids re-spending a
// Postcoder credit (and re-running the match) if the same submit attempt
// gets retried without anything changing.
const quoteSubmitCache = new Map<string, QuoteSubmitResult>();

/**
 * One call to the site's own POST /match does two jobs at once: validates
 * whichever of name/email/phone are present (phone through Postcoder, email
 * through a server-side MX lookup, name through a plausibility check) and
 * ranks installers against products/roof/postcode/usage - see the API
 * repo's handlers/match.go. Called once, at the wizard's final submit step,
 * rather than a separate validate call there and a separate match call on
 * the results page. Routed through window.ionApi.fetch, which client.js
 * injects on every page, so the session-token handshake it requires is
 * handled for us. Returns null on any failure (client.js not loaded yet,
 * network error, service down). The wizard's submit handler treats that as
 * a retryable error rather than proceeding - see the `result === null`
 * check there.
 */
async function submitQuote(
  products: Product[],
  roof: RoofType | undefined,
  postcode: string | undefined,
  usage: UsageLevel | undefined,
  name: string,
  email: string,
  phone: string
): Promise<QuoteSubmitResult | null> {
  const cacheKey = JSON.stringify([products, roof, postcode, usage, name, email, phone]);
  const cached = quoteSubmitCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  if (!window.ionApi) {
    return null;
  }
  try {
    const res = await window.ionApi.fetch("/match", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        products,
        roof: roof ?? "",
        postcode: postcode ?? "",
        usage: usage ?? "",
        name,
        email,
        phone,
        country: "GB",
      }),
    });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    const result: QuoteSubmitResult = {
      matches: Array.isArray(data.matches) ? (data.matches as MatchedInstaller[]) : undefined,
      name: typeof data.name?.valid === "boolean" ? data.name.valid : undefined,
      email: typeof data.email?.valid === "boolean" ? data.email.valid : undefined,
      phone: typeof data.phone?.valid === "boolean" ? data.phone.valid : undefined,
    };
    quoteSubmitCache.set(cacheKey, result);
    return result;
  } catch {
    return null;
  }
}

const INTEREST_STORAGE_KEY = "quoteInterests";
const ROOF_STORAGE_KEY = "quoteRoof";
const POSTCODE_STORAGE_KEY = "quotePostcode";
const USAGE_STORAGE_KEY = "quoteUsage";
const MATCHES_STORAGE_KEY = "quoteMatches";
/**
 * Set only once the quote wizard's final step actually succeeds (ion:form:success),
 * i.e. a lead has genuinely been captured and sold. INTEREST_STORAGE_KEY alone isn't
 * enough to gate /matches/ on: it's written as soon as someone ticks boxes on the
 * homepage interest picker, step 1 of 5, well before name/email/phone/consent are
 * collected. Gating on that would let anyone see their matches without ever
 * submitting contact details, skipping the lead capture the whole site exists for.
 */
const QUOTE_SUBMITTED_STORAGE_KEY = "quoteSubmitted";

const INTEREST_LABELS: Record<string, string> = {
  solar: "solar panels",
  battery: "battery storage",
  "heat-pump": "an air source heat pump",
  "ev-charger": "an EV charger",
};

const PRODUCT_ORDER: Product[] = ["solar", "battery", "heat-pump", "ev-charger"];

function initInterestForm() {
  const form = document.getElementById("interest-form");
  if (!(form instanceof HTMLFormElement)) return;

  const error = document.getElementById("interest-error");

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const checkedInputs = Array.from(
      form.querySelectorAll<HTMLInputElement>('input[name="interest"]:checked')
    );

    if (checkedInputs.length === 0) {
      if (error) error.hidden = false;
      return;
    }

    // Some cards bundle a second product into one choice (e.g. "Solar + battery"
    // only exposes a "battery" checkbox), declared via data-implies so the
    // summary and installer matching both see every product the visitor asked for.
    const values = new Set<string>();
    checkedInputs.forEach((input) => {
      values.add(input.value);
      if (input.dataset.implies) values.add(input.dataset.implies);
    });
    const checked = PRODUCT_ORDER.filter((product) => values.has(product));

    if (error) error.hidden = true;
    sessionStorage.setItem(INTEREST_STORAGE_KEY, JSON.stringify(checked));
    window.location.href = "/quote/";
  });
}

function stepIsValid(step: HTMLElement): boolean {
  const options = step.querySelector<HTMLElement>(".wizard-options");
  if (options) {
    return options.querySelectorAll('input:checked').length > 0;
  }

  const fields = step.querySelectorAll<HTMLInputElement>(".wizard-fields input, .wizard-consent input");
  if (fields.length > 0) {
    return Array.from(fields).every((field) => field.checkValidity());
  }

  return true;
}

/**
 * Catches structurally-valid but obviously fake 11-digit UK numbers that the
 * quote form's phone pattern (01|02|07 + 9 digits) lets through: the 9 digits
 * after the prefix all the same (07111111111, 07000000000), and Ofcom's
 * ranges reserved for fiction/drama use (07700 900000-900999, 01632
 * 960000-969999).
 *
 * The repeated-digit check is deliberately anchored on the 9 digits *after*
 * the mandatory (01|02|07) prefix, not all 11: the prefix's own two digits
 * (e.g. "0" then "7") can never be equal to each other, so a check requiring
 * all 11 digits to match one another can never fire for anything that has
 * already passed the pattern gate above, silently letting every
 * repeated-digit fake number through.
 */
function isPlausibleUkPhone(digits: string): boolean {
  if (/^(01|02|07)(\d)\2{8}$/.test(digits)) return false;
  if (/^07700900\d{3}$/.test(digits)) return false;
  if (/^0163296\d{4}$/.test(digits)) return false;
  return true;
}

function initFieldErrors(step: HTMLElement) {
  const phone = step.querySelector<HTMLInputElement>('.wizard-fields input[name="phone"]');
  phone?.addEventListener("input", () => {
    const digitsOnly = phone.value.replace(/[^0-9]/g, "");
    if (digitsOnly !== phone.value) phone.value = digitsOnly;
    if (phone.value.trim() === "" || phone.validity.patternMismatch) {
      phone.setCustomValidity("");
      return;
    }
    phone.setCustomValidity(isPlausibleUkPhone(phone.value) ? "" : "Not a real UK phone number");
  });

  const postcode = step.querySelector<HTMLInputElement>('.wizard-fields input[name="postcode"]');
  postcode?.addEventListener("input", () => {
    if (postcode.value.trim() === "" || postcode.validity.patternMismatch) {
      postcode.setCustomValidity("");
      return;
    }
    postcode.setCustomValidity(isKnownUkPostcodeArea(postcode.value) ? "" : "Not a recognised UK postcode area");
  });

  // Format + area-code checks above are instant and offline. Only postcodes
  // that already pass those go to the network, and only on blur (not per
  // keystroke), so this never adds latency to typing or to known-bad input.
  let lastVerified = "";
  postcode?.addEventListener("blur", () => {
    if (!postcode.validity.valid) return;
    const cleaned = postcode.value.trim().toUpperCase().replace(/\s+/g, "");
    if (cleaned === lastVerified) return;
    lastVerified = cleaned;
    verifyPostcodeExists(cleaned).then((exists) => {
      if (lastVerified !== cleaned) return; // superseded by a later edit
      // exists === null means the API was unreachable/timed out: keep the
      // offline verdict (already valid) rather than block on a dead service.
      postcode.setCustomValidity(exists === false ? "Not a real UK postcode" : "");
      const fieldError = step.querySelector<HTMLElement>('[data-field-error="postcode"]');
      if (fieldError) fieldError.hidden = postcode.validity.valid;
    });
  });

  const fields = step.querySelectorAll<HTMLInputElement>(".wizard-fields input, .wizard-consent input");
  fields.forEach((field) => {
    const fieldError = step.querySelector<HTMLElement>(`[data-field-error="${field.name}"]`);
    if (!fieldError) return;
    const update = () => {
      const isEmpty = field.type === "checkbox" ? !field.checked : field.value === "";
      fieldError.hidden = field.validity.valid || isEmpty;
    };
    field.addEventListener("input", update);
    field.addEventListener("change", update);
    field.addEventListener("blur", update);
  });
}

function initQuoteWizard() {
  const wizard = document.getElementById("quote-wizard");
  if (!(wizard instanceof HTMLFormElement)) return;

  const steps = Array.from(wizard.querySelectorAll<HTMLElement>(".wizard-step"));
  let current = 0;

  function show(index: number) {
    steps.forEach((step, i) => {
      step.hidden = i !== index;
    });
  }

  steps.forEach((step) => {
    const next = step.querySelector<HTMLButtonElement>(".wizard-next");
    const back = step.querySelector<HTMLButtonElement>(".wizard-back");
    const stepError = step.querySelector<HTMLElement>("[data-step-error]");
    const matchError = step.querySelector<HTMLElement>("[data-match-error]");
    initFieldErrors(step);

    next?.addEventListener("click", (event) => {
      if (!stepIsValid(step)) {
        event.preventDefault();
        const fields = Array.from(
          step.querySelectorAll<HTMLInputElement>(".wizard-fields input, .wizard-consent input")
        );
        fields.forEach((field) => {
          const fieldError = step.querySelector<HTMLElement>(`[data-field-error="${field.name}"]`);
          if (fieldError) fieldError.hidden = field.checkValidity();
        });
        if (stepError) stepError.hidden = false;
        return;
      }
      if (stepError) stepError.hidden = true;

      const rents = step.querySelector<HTMLInputElement>('input[name="homeowner"][value="no"]:checked');
      if (rents) {
        event.preventDefault();
        window.location.href = "/renting/";
        return;
      }

      if (current < steps.length - 1) {
        event.preventDefault();
        current += 1;
        show(current);
        return;
      }

      // Last step: gated on the live phone check below, so always intercept the
      // native submit here even though the offline checks already passed.
      event.preventDefault();

      const finishSubmit = (matches?: MatchedInstaller[]) => {
        // Native submit proceeds to the platform's injected
        // data-ion-form="subscribe" handler, which owns validation, the
        // nonce, and lead delivery. We just stash the fields the matches
        // page needs that aren't part of the lead payload itself.
        const roof = wizard.querySelector<HTMLInputElement>('input[name="roof"]:checked');
        if (roof) sessionStorage.setItem(ROOF_STORAGE_KEY, roof.value);
        const usage = wizard.querySelector<HTMLInputElement>('input[name="usage"]:checked');
        // "Not sure" is treated as "low" for matching purposes, per the site owner: a visitor
        // who doesn't know their bill shouldn't get routed to installers targeting high-usage
        // households.
        if (usage) sessionStorage.setItem(USAGE_STORAGE_KEY, usage.value === "not-sure" ? "low" : usage.value);
        const postcode = wizard.querySelector<HTMLInputElement>('input[name="postcode"]');
        if (postcode) sessionStorage.setItem(POSTCODE_STORAGE_KEY, postcode.value);
        // Matches were already fetched below, in the same POST /match call
        // that validated the contact fields, so the results page just reads
        // them back rather than calling /match a second time.
        if (matches) sessionStorage.setItem(MATCHES_STORAGE_KEY, JSON.stringify(matches));
        wizard.requestSubmit(next);
      };

      const name = step.querySelector<HTMLInputElement>('.wizard-fields input[name="name"]');
      const email = step.querySelector<HTMLInputElement>('.wizard-fields input[name="email"]');
      const phone = step.querySelector<HTMLInputElement>('.wizard-fields input[name="phone"]');
      if (!name && !email && !phone) {
        finishSubmit();
        return;
      }

      let interests: Product[] = [];
      const rawInterests = sessionStorage.getItem(INTEREST_STORAGE_KEY);
      if (rawInterests) {
        try {
          interests = JSON.parse(rawInterests);
        } catch {}
      }
      const roofInput = wizard.querySelector<HTMLInputElement>('input[name="roof"]:checked');
      const usageInput = wizard.querySelector<HTMLInputElement>('input[name="usage"]:checked');
      const postcodeInput = wizard.querySelector<HTMLInputElement>('input[name="postcode"]');

      // Real check against the site's own /match happens here, on click,
      // rather than on blur: it costs a network round trip plus a
      // proof-of-work solve, so it's only worth paying once, right before
      // these details actually get sold as a lead. Ranks installers in the
      // same call, so the results page doesn't need a second one.
      const originalLabel = next.innerHTML;
      next.disabled = true;
      next.textContent = "Checking your details...";
      if (matchError) matchError.hidden = true;
      submitQuote(
        interests,
        (roofInput?.value as RoofType) || undefined,
        postcodeInput?.value || undefined,
        usageInput ? (usageInput.value === "not-sure" ? "low" : (usageInput.value as UsageLevel)) : undefined,
        name?.value ?? "",
        email?.value ?? "",
        phone?.value ?? ""
      ).then((result) => {
        next.disabled = false;
        next.innerHTML = originalLabel;

        // result === null means the API was unreachable/unconfigured (network
        // error, service down, or a non-OK response). Unlike the field checks
        // below, this can't fall back to an offline verdict, and silently
        // continuing would submit the lead with no matches and land the visitor
        // on /matches/ seeing "we couldn't find a match" - indistinguishable
        // from a genuine zero-match combination. Surface it as a retryable
        // error instead of proceeding.
        if (result === null) {
          if (matchError) matchError.hidden = false;
          return;
        }

        // A field result of undefined means that field wasn't sent (missing
        // from the form).
        const failures: { field: HTMLInputElement; message: string }[] = [];
        if (name && result?.name === false) {
          failures.push({ field: name, message: "Doesn't look like a real name" });
        }
        if (email && result?.email === false) {
          failures.push({ field: email, message: "That email domain doesn't accept mail" });
        }
        if (phone && result?.phone === false) {
          failures.push({ field: phone, message: "Not a real UK phone number" });
        }

        if (failures.length > 0) {
          failures.forEach(({ field, message }) => {
            field.setCustomValidity(message);
            const fieldError = step.querySelector<HTMLElement>(`[data-field-error="${field.name}"]`);
            if (fieldError) fieldError.hidden = false;
          });
          if (stepError) stepError.hidden = false;
          return;
        }

        if (name) name.setCustomValidity("");
        if (email) email.setCustomValidity("");
        if (phone) phone.setCustomValidity("");
        finishSubmit(result?.matches);
      });
    });

    back?.addEventListener("click", () => {
      if (current > 0) {
        current -= 1;
        show(current);
      }
    });
  });

  wizard.addEventListener("ion:form:success", () => {
    sessionStorage.setItem(QUOTE_SUBMITTED_STORAGE_KEY, "true");
    window.location.href = "/matches/";
  });

  const summary = document.getElementById("wizard-summary");
  const raw = sessionStorage.getItem(INTEREST_STORAGE_KEY);
  if (raw) {
    try {
      const values: string[] = JSON.parse(raw);
      const interestsField = wizard.querySelector<HTMLInputElement>("#wizard-interests-field");
      if (interestsField) interestsField.value = values.join(", ");

      const labels = values.map((value) => INTEREST_LABELS[value] || value);
      if (labels.length > 0 && summary) {
        summary.innerHTML = `Getting you matched for <strong>${labels.join(", ")}</strong>.`;
      }
    } catch {}
  }
}

// Years active is computed at render time from activeSince rather than hardcoded,
// so the figure never goes stale.
function installerStrengthBullets(installer: MatchedInstaller): string[] {
  const bullets = [...(installer.strengths ?? [])];
  if (installer.activeSince !== undefined) {
    const years = new Date().getFullYear() - installer.activeSince;
    bullets.push(`${years}+ years active (since ${installer.activeSince})`);
  }
  return bullets;
}

function initInstallerMatches() {
  const container = document.getElementById("installer-matches");
  if (!container) return;

  // Gate on an actual completed submission, not just chosen interests: sends anyone
  // who reaches /matches/ without having submitted the wizard (a bookmark, a shared
  // link, a stale tab from a previous visit) back to start it, rather than showing
  // matches for free. replace() so Back doesn't bounce them straight into this page again.
  if (sessionStorage.getItem(QUOTE_SUBMITTED_STORAGE_KEY) !== "true") {
    window.location.replace("/request-a-quote/");
    return;
  }

  let interests: string[] = [];
  const raw = sessionStorage.getItem(INTEREST_STORAGE_KEY);
  if (raw) {
    try {
      interests = JSON.parse(raw);
    } catch {}
  }

  if (interests.length === 0) {
    container.innerHTML = `<p class="matches-empty">We don't have your answers yet. <a href="/request-a-quote/">Start the quote form</a> to get matched.</p>`;
    return;
  }

  // Matches were already fetched, in the same POST /match call that
  // validated the wizard's contact fields, and stashed in sessionStorage at
  // submit time (see initQuoteWizard's finishSubmit) - no second API call
  // needed just to render this page. An outright /match failure is now
  // caught at submit time (see the `result === null` check there) and never
  // gets this far, so reaching here with matches missing means the API
  // responded but sent back no matches or a malformed matches field: show
  // the same "couldn't find a match" message a genuine zero-match
  // combination would.
  let matches: MatchedInstaller[] = [];
  const rawMatches = sessionStorage.getItem(MATCHES_STORAGE_KEY);
  if (rawMatches) {
    try {
      matches = JSON.parse(rawMatches);
    } catch {}
  }

  if (matches.length === 0) {
    container.innerHTML = `<p class="matches-empty">We couldn't find a match for that combination yet. <a href="/request-a-quote/">Try again</a>.</p>`;
    return;
  }

  container.innerHTML = matches
    .map(
      (installer) => `
    <div class="installer-card">
      <div class="installer-card-header">
        <h3>${installer.name}</h3>
        ${installer.tier ? `<span class="installer-coverage-badge">${installer.tier === "big" ? "Nationwide" : "Local installer"}</span>` : ""}
      </div>
      <ul class="installer-strengths">
        ${installerStrengthBullets(installer).map((strength) => `<li>${strength}</li>`).join("")}
      </ul>
    </div>`
    )
    .join("");
}

const prefersReducedMotion =
  typeof window !== "undefined" && "matchMedia" in window
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

/**
 * Animates a result figure counting up (or down) from whatever it last showed to
 * `to`, formatting every intermediate frame with `format`. The element's previous
 * value is tracked in a data attribute so repeated calls (e.g. on every "input"
 * event) tween from where the number actually is, not from zero each time.
 * Jumps straight to the final value under prefers-reduced-motion.
 */
function animateResultNumber(el: HTMLElement, to: number, format: (value: number) => string) {
  const from = Number(el.dataset.animatedValue ?? 0);
  el.dataset.animatedValue = String(to);

  if (prefersReducedMotion || from === to) {
    el.textContent = format(to);
    return;
  }

  const tween = { value: from };
  animate(tween, {
    value: to,
    duration: 600,
    ease: "outExpo",
    onUpdate: () => {
      el.textContent = format(tween.value);
    },
  });
}

/**
 * Energy cost & solar savings calculator (content/pages/energy-cost-calculator.md).
 * The actual numbers come from computeEnergyEstimate (energy-calculator.ts);
 * this just wires the form's inputs and outputs to it.
 */
function initEnergyCalculator() {
  const form = document.getElementById("energy-calculator");
  if (!form) return;

  const sizeInputs = form.querySelectorAll<HTMLInputElement>('input[name="household-size"]');
  const modeInputs = form.querySelectorAll<HTMLInputElement>('input[name="calc-mode"]');
  const postcodeInput = document.getElementById("calc-postcode") as HTMLInputElement | null;
  const knownValueInput = document.getElementById("calc-known-value") as HTMLInputElement | null;
  const knownValueLabel = document.getElementById("calc-known-value-label");
  const sizeHint = document.getElementById("calc-size-hint");

  const outUsage = document.getElementById("calc-out-usage");
  const outCost = document.getElementById("calc-out-cost");
  const outSystem = document.getElementById("calc-out-system");
  const outSaving = document.getElementById("calc-out-saving");
  const outSavingPct = document.getElementById("calc-out-saving-pct");
  if (!outUsage || !outCost || !outSystem || !outSaving || !outSavingPct) return;

  form.addEventListener("submit", (event) => event.preventDefault());

  function selectedSize(): HouseholdSize {
    const checked = Array.from(sizeInputs).find((input) => input.checked);
    return (checked?.value as HouseholdSize) ?? "medium";
  }

  function currentMode(): CalcMode {
    const checked = Array.from(modeInputs).find((input) => input.checked);
    return checked?.value === "bill" ? "bill" : "usage";
  }

  function formatGbp(amount: number): string {
    return `£${Math.round(amount).toLocaleString("en-GB")}`;
  }

  function update() {
    const mode = currentMode();
    if (knownValueLabel) {
      knownValueLabel.textContent =
        mode === "bill" ? "Annual electricity bill in £ (optional)" : "Annual electricity use in kWh (optional)";
    }
    if (knownValueInput) {
      knownValueInput.placeholder = mode === "bill" ? "e.g. 900" : "e.g. 2500";
    }

    const rawValue = knownValueInput?.value.trim() ?? "";
    const estimate = computeEnergyEstimate({
      size: selectedSize(),
      mode,
      knownValue: rawValue === "" ? null : Number(rawValue),
      postcode: postcodeInput?.value ?? "",
    });

    // Household size caps how big a system that home could realistically fit; this only
    // shows up when a declared usage/bill would otherwise call for more roof than that.
    if (sizeHint) sizeHint.hidden = !estimate.roofCapped;

    animateResultNumber(outUsage!, estimate.annualKwh, (value) => `${Math.round(value).toLocaleString("en-GB")} kWh`);
    animateResultNumber(outCost!, estimate.annualCost, formatGbp);
    animateResultNumber(outSystem!, estimate.systemKw, (value) => `${value.toFixed(1)}kWp`);
    animateResultNumber(outSaving!, estimate.totalSaving, (value) => `${formatGbp(value)} a year`);
    animateResultNumber(outSavingPct!, estimate.savingPct, (value) => `${Math.round(value)}%`);

    // Lets the EV charging comparison tool personalize its solar column for a
    // returning visitor instead of falling back to the site-wide default share.
    sessionStorage.setItem(SELF_CONSUMPTION_SHARE_STORAGE_KEY, String(estimate.selfConsumptionShare));
  }

  form.addEventListener("input", update);
  update();
}

/** Formats a pence value under £1 as "6.0p", £1 and over as "£1.25". Used for per-use/per-day figures, which are often small. */
function formatPenceOrGbp(pence: number): string {
  return pence >= 100 ? `£${(pence / 100).toFixed(2)}` : `${pence.toFixed(1)}p`;
}

/**
 * Appliance running-cost calculator (content/pages/appliance-running-costs.md).
 * The numbers come from computeApplianceCost (appliance-cost-calculator.ts);
 * this wires the appliance dropdown and its fields to it, and writes out the
 * watts/1000 * hours = kWh, kWh * unit rate = cost formula with real numbers
 * plugged in on every change.
 */
function initApplianceCalculator() {
  const form = document.getElementById("appliance-calculator");
  if (!form) return;

  const select = document.getElementById("appliance-select") as HTMLSelectElement | null;
  const wattsInput = document.getElementById("appliance-watts") as HTMLInputElement | null;
  const hoursInput = document.getElementById("appliance-hours") as HTMLInputElement | null;
  const usesInput = document.getElementById("appliance-uses") as HTMLInputElement | null;
  const formulaEl = document.getElementById("appliance-formula");
  const outPerUse = document.getElementById("appliance-out-per-use");
  const outPerDay = document.getElementById("appliance-out-per-day");
  const outPerYear = document.getElementById("appliance-out-per-year");
  const highLoadNote = document.getElementById("appliance-highload-note");
  if (!select || !wattsInput || !hoursInput || !usesInput || !formulaEl || !outPerUse || !outPerDay || !outPerYear) return;

  select.innerHTML = APPLIANCE_PRESETS.map((preset) => `<option value="${preset.id}">${preset.label}</option>`).join("");

  function applyPreset(id: string) {
    const preset = findAppliancePreset(id);
    if (!preset) return;
    wattsInput!.value = String(preset.watts);
    hoursInput!.value = String(preset.defaultHoursPerUse);
    usesInput!.value = String(preset.defaultUsesPerDay);
  }

  applyPreset(select.value);

  select.addEventListener("change", () => {
    applyPreset(select.value);
    update();
  });

  function update() {
    const watts = Number(wattsInput!.value);
    const hoursPerUse = Number(hoursInput!.value);
    const usesPerDay = Number(usesInput!.value);
    const estimate = computeApplianceCost({ watts, hoursPerUse, usesPerDay });

    formulaEl!.textContent =
      `${watts || 0}W ÷ 1000 × ${hoursPerUse || 0}h = ${estimate.kwhPerUse.toFixed(2)}kWh   ×   ${UNIT_RATE_PENCE}p/kWh = ${formatPenceOrGbp(estimate.costPerUsePence)} per use`;
    outPerUse!.textContent = formatPenceOrGbp(estimate.costPerUsePence);
    outPerDay!.textContent = formatPenceOrGbp(estimate.costPerDayPence);
    outPerYear!.textContent = `£${Math.round(estimate.costPerYearPence / 100).toLocaleString("en-GB")}`;

    const selectedPreset = findAppliancePreset(select!.value);
    if (highLoadNote) highLoadNote.hidden = !selectedPreset?.highLoad;
  }

  form.addEventListener("input", update);
  update();
}

/**
 * EV home-charging cost comparison (content/pages/ev-charging-cost-comparison.md).
 * The numbers come from computeEvCostComparison (ev-charging-cost-calculator.ts).
 * The "with your solar" column personalizes itself from the energy cost
 * calculator's last result in sessionStorage, if the visitor already used it,
 * rather than falling back to the site-wide default self-consumption share.
 */
function initEvComparisonCalculator() {
  const form = document.getElementById("ev-comparison-calculator");
  if (!form) return;

  const select = document.getElementById("ev-select") as HTMLSelectElement | null;
  const efficiencyInput = document.getElementById("ev-efficiency") as HTMLInputElement | null;
  const solarBadge = document.getElementById("ev-solar-badge");
  const solarNote = document.getElementById("ev-solar-note");
  const outOvernightPence = document.getElementById("ev-out-overnight-pence");
  const outOvernightYear = document.getElementById("ev-out-overnight-year");
  const outSolarPence = document.getElementById("ev-out-solar-pence");
  const outSolarYear = document.getElementById("ev-out-solar-year");
  const outStandardPence = document.getElementById("ev-out-standard-pence");
  const outStandardYear = document.getElementById("ev-out-standard-year");
  const outPublicPence = document.getElementById("ev-out-public-pence");
  const outPublicYear = document.getElementById("ev-out-public-year");
  const outPetrolPence = document.getElementById("ev-out-petrol-pence");
  const outPetrolYear = document.getElementById("ev-out-petrol-year");
  if (
    !select || !efficiencyInput ||
    !outOvernightPence || !outOvernightYear || !outSolarPence || !outSolarYear ||
    !outStandardPence || !outStandardYear || !outPublicPence || !outPublicYear ||
    !outPetrolPence || !outPetrolYear
  ) return;

  select.innerHTML = EV_PRESETS.map((preset) => `<option value="${preset.id}">${preset.label}</option>`).join("");

  function applyPreset(id: string) {
    const preset = findEvPreset(id) ?? EV_PRESETS[0];
    efficiencyInput!.value = String(preset.kwhPer100Miles);
  }

  applyPreset(select.value);

  select.addEventListener("change", () => {
    applyPreset(select.value);
    update();
  });

  const ANNUAL_MILES = 7000; // UK average car's annual mileage, a round illustrative figure

  function formatPenceLabel(pence: number): string {
    return `${pence.toFixed(1)}p`;
  }
  function formatYearLabel(pence: number): string {
    return `£${Math.round((pence / 100) * ANNUAL_MILES).toLocaleString("en-GB")}`;
  }

  function update() {
    const efficiency = Number(efficiencyInput!.value);

    const storedShareRaw = sessionStorage.getItem(SELF_CONSUMPTION_SHARE_STORAGE_KEY);
    const storedShare = storedShareRaw !== null ? Number(storedShareRaw) : null;
    const hasPersonalizedShare = storedShare !== null && Number.isFinite(storedShare);

    const result = hasPersonalizedShare
      ? computeEvCostComparison(efficiency, storedShare!)
      : computeEvCostComparison(efficiency);

    if (solarBadge) solarBadge.hidden = !hasPersonalizedShare;
    if (solarNote) {
      solarNote.textContent = hasPersonalizedShare
        ? `Personalized from your energy cost calculator result: about ${Math.round(result.solarShareUsed * 100)}% of this is assumed to run on self-used solar.`
        : "This uses the site's default solar self-consumption assumption (around 40%). Try our energy cost calculator first and this column personalizes itself to your own result.";
    }

    outOvernightPence!.textContent = formatPenceLabel(result.homeOvernightPencePerMile);
    outOvernightYear!.textContent = formatYearLabel(result.homeOvernightPencePerMile);
    outSolarPence!.textContent = formatPenceLabel(result.homeSolarPencePerMile);
    outSolarYear!.textContent = formatYearLabel(result.homeSolarPencePerMile);
    outStandardPence!.textContent = formatPenceLabel(result.homeStandardPencePerMile);
    outStandardYear!.textContent = formatYearLabel(result.homeStandardPencePerMile);
    outPublicPence!.textContent = `${formatPenceLabel(result.publicRapidLowPencePerMile)} to ${formatPenceLabel(result.publicRapidHighPencePerMile)}`;
    outPublicYear!.textContent = `${formatYearLabel(result.publicRapidLowPencePerMile)} to ${formatYearLabel(result.publicRapidHighPencePerMile)}`;
    outPetrolPence!.textContent = formatPenceLabel(result.petrolPencePerMile);
    outPetrolYear!.textContent = formatYearLabel(result.petrolPencePerMile);
  }

  form.addEventListener("input", update);
  update();
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

interface CompareRow<T> {
  label: string;
  value: (item: T) => string;
}

/**
 * Fills a <select>'s options from a model list plus a leading "None" option
 * (so a column can be left blank), and sets its starting value.
 */
function populateCompareSelect(select: HTMLSelectElement, items: { id: string; label: string }[], defaultId: string) {
  select.innerHTML =
    `<option value="">None</option>` +
    items.map((item) => `<option value="${item.id}">${escapeHtml(item.label)}</option>`).join("");
  select.value = defaultId;
}

/**
 * Renders one row per spec into `tbody`, one column per select, reading
 * whichever model is currently chosen in each (or a blank column for "None").
 * Column and row live in the same small table, so changing a dropdown
 * updates the cell right next to it instead of a results block further down
 * the page.
 */
function renderCompareTable<T>(
  selects: HTMLSelectElement[],
  tbody: HTMLElement,
  rows: CompareRow<T>[],
  findItem: (id: string) => T | undefined
) {
  const items = selects.map((select) => (select.value ? findItem(select.value) : undefined));
  tbody.innerHTML = rows
    .map((row) => {
      const cells = items.map((item) => `<td>${item ? escapeHtml(row.value(item)) : "&ndash;"}</td>`).join("");
      return `<tr><th scope="row">${escapeHtml(row.label)}</th>${cells}</tr>`;
    })
    .join("");
}

const PANEL_ROWS: CompareRow<PanelModel>[] = [
  { label: "Cell type", value: (panel) => panel.cellType },
  { label: "Wattage", value: (panel) => panel.wattage },
  { label: "Efficiency", value: (panel) => panel.efficiencyRange },
  { label: "Warranty", value: (panel) => panel.warranty },
  { label: "Best for", value: (panel) => panel.bestFor },
];

const BATTERY_ROWS: CompareRow<BatteryModel>[] = [
  { label: "Chemistry", value: (battery) => battery.chemistry },
  { label: "Usable capacity", value: (battery) => battery.usableCapacity },
  { label: "Cycle life / warranty", value: (battery) => battery.cycleLifeWarranty },
  { label: "Rough cost guide", value: (battery) => battery.costGuide },
  { label: "Best for", value: (battery) => battery.bestFor },
];

/**
 * Panel/battery model comparison tool (content/pages/panel-battery-comparison.md).
 * Three dropdowns per table, one real model each (PANEL_MODELS/BATTERY_MODELS
 * from panel-battery-comparison.ts); picking a model fills that column of the
 * same table the dropdown sits in, so the whole thing stays in one compact
 * widget rather than a picker followed by a separate results section.
 */
function initPanelBatteryComparator() {
  const panelBody = document.getElementById("panel-compare-body");
  const batteryBody = document.getElementById("battery-compare-body");
  if (!panelBody || !batteryBody) return;

  const panelSelects = [0, 1, 2]
    .map((i) => document.getElementById(`panel-col-${i}`))
    .filter((el): el is HTMLSelectElement => el instanceof HTMLSelectElement);
  const batterySelects = [0, 1, 2]
    .map((i) => document.getElementById(`battery-col-${i}`))
    .filter((el): el is HTMLSelectElement => el instanceof HTMLSelectElement);
  if (panelSelects.length !== 3 || batterySelects.length !== 3) return;

  const PANEL_DEFAULTS = ["ja-solar-deepblue", "jinko-tiger-neo", "rec-alpha-pure-r"];
  const BATTERY_DEFAULTS = ["puredrive-duracell", "solax-triple-power", "tesla-powerwall-3"];

  panelSelects.forEach((select, i) => populateCompareSelect(select, PANEL_MODELS, PANEL_DEFAULTS[i]));
  batterySelects.forEach((select, i) => populateCompareSelect(select, BATTERY_MODELS, BATTERY_DEFAULTS[i]));

  const renderPanels = () => renderCompareTable(panelSelects, panelBody, PANEL_ROWS, findPanelModel);
  const renderBatteries = () => renderCompareTable(batterySelects, batteryBody, BATTERY_ROWS, findBatteryModel);

  panelSelects.forEach((select) => select.addEventListener("change", renderPanels));
  batterySelects.forEach((select) => select.addEventListener("change", renderBatteries));

  renderPanels();
  renderBatteries();
}

ready(() => {
  initInterestForm();
  initQuoteWizard();
  initInstallerMatches();
  initEnergyCalculator();
  initApplianceCalculator();
  initEvComparisonCalculator();
  initPanelBatteryComparator();
});
